// © 2026 Buddy Media LLC. All rights reserved.
import { useEffect, useState } from "react";

import { AdminCard } from "@/components/AdminCard";
import { AdminPageShell } from "@/components/AdminPageShell";
import { apiFetch, describeApiError } from "@/lib/api";

// /admin/operations/ops-agents — per-host ops-agent dashboard.
//
// Reads from /api/admin/ops-agents which returns rows from the
// self-registered ops_agents table (env-scoped to whatever the
// backend's BW_ENV is). Role-based identities, not topology-based,
// so the same admin UI works on dev/test/prod and on AWS later.

type AgentRow = {
  id: number;
  env: string;
  role: string;
  url: string;
  hostname: string;
  notes: string | null;
  first_registered_at: string;
  last_seen_at: string;
};

type AgentInfo = {
  version: string;
  uptime_seconds: number;
  bearer_present: boolean;
  bearer_jti: string | null;
  pat_cached: boolean;
  allowed_credential_keys: string[];
};

type ProxyEnvelope<T> = {
  ok: boolean;
  role: string;
  url: string;
  status_code?: number;
  body?: T;
  error?: string;
};

const describeError = describeApiError;

function formatUptime(s: number): string {
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  if (s < 86400) {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return `${h}h ${m}m`;
  }
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  return `${d}d ${h}h`;
}

function relativeAge(iso: string): string {
  const then = new Date(iso).getTime();
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

export function OpsAgentsPage() {
  const [agents, setAgents] = useState<AgentRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    apiFetch<AgentRow[]>("/api/admin/ops-agents")
      .then(setAgents)
      .catch((err) => setError(describeError(err, "Couldn't load registry")));
  }, [refreshKey]);

  return (
    <AdminPageShell
      title="Ops agents"
      description="Per-host ops-agent dashboard. Agents self-register on startup; the registry is env-scoped (this backend's BW_ENV), role-based (env-portable), and shows live /admin/info per agent. Closes the SSH hole for credential rotation + agent-side ops."
    >
      {error ? (
        <AdminCard className="border-rose-900/40 bg-rose-950/20">
          <p className="text-sm text-rose-200">{error}</p>
        </AdminCard>
      ) : null}

      {agents === null ? (
        <AdminCard>
          <p className="text-sm text-slate-400">Loading registry…</p>
        </AdminCard>
      ) : agents.length === 0 ? (
        <AdminCard>
          <p className="text-sm text-slate-400">
            No agents registered yet. Run{" "}
            <code className="font-mono text-slate-300">
              bash scripts/install-ops-agent.sh
            </code>{" "}
            on each host. The agent self-registers at startup.
          </p>
        </AdminCard>
      ) : (
        <div className="space-y-4">
          {agents.map((row) => (
            <AgentCard
              key={row.id}
              row={row}
              onChanged={() => setRefreshKey((k) => k + 1)}
            />
          ))}
        </div>
      )}
    </AdminPageShell>
  );
}

function AgentCard({
  row,
  onChanged,
}: {
  row: AgentRow;
  onChanged: () => void;
}) {
  const [info, setInfo] = useState<AgentInfo | null>(null);
  const [probeOk, setProbeOk] = useState<boolean | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    setErrorMessage(null);
    try {
      const infoRes = await apiFetch<ProxyEnvelope<AgentInfo>>(
        `/api/admin/ops-agents/${row.role}/info`,
      );
      if (!infoRes.ok) {
        setErrorMessage(
          infoRes.error || `agent /admin/info failed (HTTP ${infoRes.status_code})`,
        );
        setInfo(null);
      } else if (infoRes.body) {
        setInfo(infoRes.body);
      }
      const probeRes = await apiFetch<ProxyEnvelope<{ ok: boolean }>>(
        `/api/admin/ops-agents/${row.role}/probe-bw`,
      );
      setProbeOk(probeRes.ok && (probeRes.body?.ok ?? false));
    } catch (err) {
      setErrorMessage(describeError(err, "Couldn't load agent info"));
      setInfo(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.role]);

  const reachable = info !== null;

  return (
    <AdminCard className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-white">{row.role}</h3>
            <EnvBadge env={row.env} />
            <ReachBadge reachable={reachable} loading={loading} />
          </div>
          <div className="text-xs text-slate-500">
            <code className="font-mono text-slate-400">{row.hostname}</code> ·{" "}
            <code className="font-mono text-slate-400">{row.url}</code>
            <span className="ml-2 text-slate-600">
              first registered {relativeAge(row.first_registered_at)} · last
              seen {relativeAge(row.last_seen_at)}
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={refresh}
            disabled={loading}
            className="rounded-md border border-slate-700 bg-slate-900 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-800 disabled:opacity-50"
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
          <DeleteButton id={row.id} role={row.role} onDeleted={onChanged} />
        </div>
      </div>

      {errorMessage ? (
        <p className="text-xs text-rose-300">{errorMessage}</p>
      ) : null}

      {info ? (
        <div className="grid gap-2 text-xs sm:grid-cols-2">
          <Field label="Version" value={info.version} />
          <Field label="Uptime" value={formatUptime(info.uptime_seconds)} />
          <Field
            label="Bearer present"
            value={info.bearer_present ? "yes" : "no"}
            tone={info.bearer_present ? "ok" : "warn"}
          />
          <Field label="Bearer JTI" value={info.bearer_jti ?? "—"} mono />
          <Field
            label="PAT cached"
            value={info.pat_cached ? "yes" : "no"}
            tone={info.pat_cached ? "ok" : "neutral"}
          />
          <Field
            label="BW probe"
            value={
              probeOk === null
                ? "—"
                : probeOk
                  ? "accepted by BW"
                  : "rejected"
            }
            tone={probeOk === null ? "neutral" : probeOk ? "ok" : "warn"}
          />
        </div>
      ) : null}

      {info ? <RotateBearerForm role={row.role} onSuccess={refresh} /> : null}
    </AdminCard>
  );
}

function Field({
  label,
  value,
  tone = "neutral",
  mono = false,
}: {
  label: string;
  value: string;
  tone?: "ok" | "warn" | "neutral";
  mono?: boolean;
}) {
  const valueClass =
    tone === "ok"
      ? "text-emerald-300"
      : tone === "warn"
        ? "text-amber-300"
        : "text-slate-200";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className={`${mono ? "font-mono" : ""} ${valueClass}`}>{value}</div>
    </div>
  );
}

function EnvBadge({ env }: { env: string }) {
  const tone =
    env === "prod"
      ? "bg-rose-900/60 text-rose-200"
      : env === "test"
        ? "bg-amber-900/60 text-amber-200"
        : "bg-cyan-900/60 text-cyan-200";
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wide ${tone}`}
    >
      {env}
    </span>
  );
}

function ReachBadge({
  reachable,
  loading,
}: {
  reachable: boolean;
  loading: boolean;
}) {
  if (loading) {
    return (
      <span className="rounded-full bg-slate-800 px-2 py-0.5 text-[10px] uppercase tracking-wide text-slate-400">
        probing
      </span>
    );
  }
  return reachable ? (
    <span className="rounded-full bg-emerald-900/60 px-2 py-0.5 text-[10px] uppercase tracking-wide text-emerald-200">
      reachable
    </span>
  ) : (
    <span className="rounded-full bg-rose-900/60 px-2 py-0.5 text-[10px] uppercase tracking-wide text-rose-200">
      unreachable
    </span>
  );
}

function DeleteButton({
  id,
  role,
  onDeleted,
}: {
  id: number;
  role: string;
  onDeleted: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (
      !window.confirm(
        `Remove ${role} from the registry? Agent process keeps running; the row just stops appearing here until it next registers.`,
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await apiFetch(`/api/admin/ops-agents/${id}`, { method: "DELETE" });
      onDeleted();
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={submit}
      disabled={busy}
      className="rounded-md border border-rose-900/40 bg-rose-950/20 px-3 py-1.5 text-xs text-rose-200 hover:bg-rose-950/30 disabled:opacity-50"
    >
      {busy ? "Removing…" : "Remove"}
    </button>
  );
}

function RotateBearerForm({
  role,
  onSuccess,
}: {
  role: string;
  onSuccess: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const submit = async () => {
    if (!value.trim()) return;
    setBusy(true);
    setMessage(null);
    try {
      const res = await apiFetch<
        ProxyEnvelope<{ ok: boolean; bearer_jti: string | null }>
      >(`/api/admin/ops-agents/${role}/credentials`, {
        method: "POST",
        body: JSON.stringify({
          field: "BW_OPS_AGENT_BACKEND_BEARER",
          value: value.trim(),
        }),
      });
      if (!res.ok) {
        setMessage(res.error || `rotate failed (HTTP ${res.status_code})`);
      } else {
        setMessage(
          `rotated — new JTI ${res.body?.bearer_jti ?? "(unknown)"}`,
        );
        setValue("");
        setOpen(false);
        onSuccess();
      }
    } catch (err) {
      setMessage(describeError(err, "Rotate failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-t border-slate-800 pt-3">
      {open ? (
        <div className="space-y-2">
          <label className="block text-xs uppercase tracking-wide text-slate-500">
            New BW_OPS_AGENT_BACKEND_BEARER
          </label>
          <textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            rows={3}
            placeholder="Paste a freshly minted service-account bearer (JWT)"
            className="w-full rounded-md border border-slate-800 bg-slate-950 px-3 py-2 font-mono text-xs text-slate-200 placeholder-slate-600"
          />
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={submit}
              disabled={busy || !value.trim()}
              className="rounded-md bg-pink-500 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
            >
              {busy ? "Rotating…" : "Rotate bearer"}
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setValue("");
              }}
              className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300"
            >
              Cancel
            </button>
            <p className="text-[10px] text-slate-500">
              Atomic env-file rewrite + cache flush — no container restart, no SSH.
            </p>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="rounded-md border border-slate-700 bg-slate-900 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-800"
        >
          Rotate outgoing bearer
        </button>
      )}
      {message ? (
        <p className="mt-2 text-xs text-slate-300">{message}</p>
      ) : null}
    </div>
  );
}
